"use client"

import {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  type ReactNode,
} from "react"
import { useNavigate, useLocation } from "react-router-dom"
import { SWRConfig } from "swr"
import { AuthProvider } from "@/lib/auth-context"
import { DemoProvider } from "@/lib/demo-context"
import { ToastProvider } from "@/lib/hooks/use-toast"

/* ── Derived app mode from pathname ── */
export type AppMode = "chat-landing" | "chat-session" | "skills" | "community" | "courses" | "orders" | "creator"

function modeFromPathname(pathname: string): AppMode {
  if (pathname.startsWith("/academy")) return "courses"
  if (pathname.startsWith("/community")) return "community"
  if (pathname.startsWith("/skills")) return "skills"
  if (pathname.startsWith("/creator-center")) return "creator"
  if (pathname.startsWith("/chat")) return "chat-session"
  if (pathname.startsWith("/orders") || pathname.startsWith("/account-center")) return "orders"
  return "chat-landing"
}

/* ── Context shape (slimmed — auth & demo are separate contexts) ── */
interface AppContextValue {
  /** Current app mode derived from URL */
  mode: AppMode
  /** True while a route transition is in-flight */
  isNavigating: boolean

  /* ── Theme ── */
  isDark: boolean
  mounted: boolean
  toggleTheme: () => void

  /* ── Navigation ── */
  goToLanding: () => void
  goToChat: () => void
  goToSkills: () => void
  goToCommunity: () => void
  goToProfile: () => void
  goToCourses: () => void
  goToOrders: () => void

  /* ── Wave background intensity (0–1, default 1) ── */
  waveIntensity: number
  setWaveIntensity: (v: number) => void
}

const AppContext = createContext<AppContextValue | null>(null)

export function useAppContext() {
  const ctx = useContext(AppContext)
  if (!ctx) throw new Error("useAppContext must be used within AppProvider")
  return ctx
}

/* ── Provider ── */
export function AppProvider({ children }: { children: ReactNode }) {
  const navigate = useNavigate()
  const location = useLocation()
  const mode = modeFromPathname(location.pathname)

  /* ── Theme ── */
  const [isDark, setIsDark] = useState(false)
  const [mounted, setMounted] = useState(false)

  /* ── Wave intensity (pages like skills can dim the background on scroll) ── */
  const [waveIntensity, setWaveIntensity] = useState(1)

  /* ── Init ── */
  useEffect(() => {
    setMounted(true)
  }, [])

  /* ── Track last non-workplace path for smart back button ── */
  useEffect(() => {
    if (!location.pathname.startsWith("/workplace")) {
      sessionStorage.setItem("last-internal-path", location.pathname)
    }
  }, [location.pathname])

  /* ── Theme sync ── */
  useEffect(() => {
    if (!mounted) return
    if (isDark) {
      document.documentElement.classList.add("dark")
    } else {
      document.documentElement.classList.remove("dark")
    }
  }, [isDark, mounted])

  /* ── Callbacks ── */
  const toggleTheme = useCallback(() => setIsDark((d) => !d), [])

  /* ── Navigation ── */
  const goToLanding = useCallback(() => {
    navigate("/")
  }, [navigate])

  const goToChat = useCallback(() => {
    navigate("/chat")
  }, [navigate])

  const goToSkills = useCallback(() => {
    navigate("/skills")
  }, [navigate])

  const goToCommunity = useCallback(() => {
    navigate("/community")
  }, [navigate])

  const goToProfile = useCallback(() => {
    navigate("/community/user/u-self")
  }, [navigate])

  const goToCourses = useCallback(() => {
    navigate("/academy")
  }, [navigate])

  const goToOrders = useCallback(() => {
    navigate("/account-center")
  }, [navigate])

  /* Callback for AuthProvider to handle post-logout navigation */
  const handleLogout = useCallback(() => {
    navigate("/")
  }, [navigate])

  return (
    <AppContext.Provider
      value={{
        mode,
        isNavigating: false,
        isDark,
        mounted,
        toggleTheme,
        goToLanding,
        goToChat,
        goToSkills,
        goToCommunity,
        goToProfile,
        goToCourses,
        goToOrders,
        waveIntensity,
        setWaveIntensity,
      }}
    >
      <SWRConfig value={{ dedupingInterval: 60_000, revalidateOnFocus: false }}>
        <AuthProvider onLogout={handleLogout}>
          <DemoProvider>
            <ToastProvider>
              {children}
            </ToastProvider>
          </DemoProvider>
        </AuthProvider>
      </SWRConfig>
    </AppContext.Provider>
  )
}
